<template>
  <DossierDocumentCard
    title="Termo de Visita Sanitária"
    :status-options="statusOptions"
    :multiple-status="true"
    :status-requires-validity="['possui_vigente', 'possui_provisorio']"
    :status-requires-upload="['possui_vigente', 'possui_provisorio']"
    :explainer-text="explainerText"
    :mode="mode"
    :not-applicable="false"
    :start-collapsed="startCollapsed"
    v-model="formData"
    save-endpoint="/rest-home/dossier/sanitary-visit"
    upload-endpoint="/rest-home/dossier/sanitary-visit/upload"
    @saved="onSaved"
    @error="onError"
  >
    <!-- Campos específicos: data da visita e órgão fiscalizador -->
    <template #fields-before>
      <div class="dossier-module__fields">
        <div class="dossier-module__field-group">
          <label class="dossier-module__label">Órgão fiscalizador</label>
          <v-text-field
            v-if="!isViewMode"
            v-model="formData.inspecting_body"
            dense
            outlined
            hide-details
            placeholder="Ex: Vigilância Sanitária Municipal"
            class="dossier-module__input"
          />
          <span v-else class="dossier-module__view-value">{{
            formData.inspecting_body || "—"
          }}</span>
        </div>

        <div
          class="dossier-module__field-group dossier-module__field-group--small"
        >
          <label class="dossier-module__label">Data da visita</label>
          <v-text-field
            v-if="!isViewMode"
            v-model="formData.visit_date"
            type="date"
            dense
            outlined
            hide-details
            class="dossier-module__input"
          />
          <span v-else class="dossier-module__view-value">
            <i class="fas fa-calendar-alt mr-1"></i>
            {{ formatDateBR(formData.visit_date) }}
          </span>
        </div>

        <!-- Resultado da visita -->
        <div class="dossier-module__field-group">
          <label class="dossier-module__label">Resultado da visita</label>
          <v-select
            v-if="!isViewMode"
            v-model="formData.visit_result"
            :items="visitResults"
            dense
            outlined
            hide-details
            placeholder="Selecione"
            class="dossier-module__input"
          />
          <span
            v-else
            class="dossier-module__view-value"
            :class="visitResultClass"
          >
            {{ formData.visit_result || "—" }}
          </span>
        </div>
      </div>
    </template>
  </DossierDocumentCard>
</template>

<script>
import DossierDocumentCard from "../dossier/DossierDocumentCard.vue";

const EXPLAINER_TEXT = `
  O Termo de Visita Sanitária é o documento emitido pela Vigilância Sanitária
  ao final de cada inspeção realizada na ILPI, registrando as condições
  encontradas, as pendências identificadas e o prazo para regularização.<br><br>
  Deve ser arquivado o termo da visita mais recente. Quando houver pendências
  indicadas no termo, recomenda-se manter também o comprovante de resolução.<br><br>
  O Sistema Clin não valida o conteúdo do documento, apenas registra sua
  existência para fins de organização e apresentação à fiscalização.
`;

export default {
  name: "DossierSanitaryVisit",
  components: { DossierDocumentCard },
  props: {
    mode: {
      type: String,
      default: "edit",
      validator: (v) => ["edit", "view"].includes(v),
    },
    initialData: { type: Object, default: () => ({}) },
    startCollapsed: { type: Boolean, default: false },
  },
  data() {
    return {
      explainerText: EXPLAINER_TEXT,
      statusOptions: [
        {
          value: "possui_vigente",
          label: "Possui termo vigente (sem pendências)",
        },
        {
          value: "possui_provisorio",
          label: "Possui termo com pendências em prazo",
        },
        { value: "em_elaboracao", label: "Visita agendada / em andamento" },
        { value: "nao_possui", label: "Não possui termo (pendente)" },
      ],
      visitResults: [
        "Regular",
        "Regular com pendências",
        "Irregular",
        "Aguardando resultado",
      ],
      formData: {
        inspecting_body: "",
        visit_date: null,
        visit_result: null,
        status: [],
        validity_date: null,
        file_url: null,
        file_name: null,
        observations: "",
        ...this.flattenInitialData(),
      },
    };
  },
  computed: {
    isViewMode() {
      return this.mode === "view";
    },
    visitResultClass() {
      const map = {
        Regular: "dossier-module__result--ok",
        "Regular com pendências": "dossier-module__result--warning",
        Irregular: "dossier-module__result--error",
      };
      return map[this.formData.visit_result] || "";
    },
  },
  watch: {
    initialData: {
      deep: true,
      handler() {
        Object.assign(this.formData, this.flattenInitialData());
      },
    },
  },
  methods: {
    flattenInitialData() {
      const d = this.initialData || {};
      return {
        inspecting_body: d.inspecting_body || "",
        visit_date: d.visit_date || null,
        visit_result: d.visit_result || null,
        status: d.status || [],
        validity_date: d.validity_date || null,
        file_url: d.file_url || null,
        file_name: d.file_name || null,
        observations: d.observations || "",
      };
    },
    onSaved(data) {
      this.$emit("saved", { module: "sanitary_visit", data });
    },
    onError(errors) {
      this.$emit("error", { module: "sanitary_visit", errors });
    },
    formatDateBR(dateStr) {
      if (!dateStr) return "—";
      const [y, m, d] = dateStr.split("-");
      return `${d}/${m}/${y}`;
    },
  },
};
</script>

<style scoped>
.dossier-module__result--ok {
  color: #30915b;
  font-weight: 600;
}
.dossier-module__result--warning {
  color: #fb8b01;
  font-weight: 600;
}
.dossier-module__result--error {
  color: #dc3545;
  font-weight: 600;
}
</style>